import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Check, X, Loader2, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const peso = (v) => `₱${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Compact, card-level surfacing of pending/approved EXTRA CHARGES for a client
// on an accepted (unpaid) booking, so the approval request is visible without
// opening the Booking Details modal.
const BookingCardExtraCharges = ({ booking, effectiveRole, onApprovedTotal }) => {
  const [charges, setCharges] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [err, setErr] = useState('');

  const isClient = effectiveRole === 'client';
  const isProvider = effectiveRole === 'provider';
  const eligible = (isClient || isProvider) && booking?.status === 'accepted' && booking?.payment_status !== 'paid';

  const fetchCharges = useCallback(async () => {
    if (!eligible) { setLoaded(true); return; }
    try {
      const res = await axios.get(`${API}/bookings/${booking.id}/charges`);
      const addons = (res.data.charges || []).filter((c) => c.kind === 'addon');
      setCharges(addons);
      const approved = addons.filter((c) => c.status === 'approved');
      onApprovedTotal?.(booking.id, {
        clientTotal: approved.reduce((s, c) => s + Number(c.amount || 0), 0),
        providerPayout: approved.reduce((s, c) => s + Number(c.provider_payout_amount || 0), 0),
      });
    } catch (_) {
      /* non-blocking */
    } finally {
      setLoaded(true);
    }
  }, [booking.id, eligible, onApprovedTotal]);

  useEffect(() => { fetchCharges(); }, [fetchCharges]);

  if (!eligible || !loaded) return null;

  const pending = charges.filter((c) => c.status === 'pending_approval');
  // Only the client can act on pending charges; the provider's earnings are
  // updated via the reported approved-payout total (no action block shown).
  if (!isClient || pending.length === 0) return null;

  const approve = async (id) => {
    setBusyId(id); setErr('');
    try { await axios.post(`${API}/charges/${id}/approve`, { payment_method: 'gcash' }); fetchCharges(); }
    catch (e) { setErr(e.response?.data?.detail || 'Failed to approve.'); }
    finally { setBusyId(null); }
  };

  const reject = async (id) => {
    setBusyId(id); setErr('');
    try { await axios.post(`${API}/charges/${id}/reject`); fetchCharges(); }
    catch (e) { setErr(e.response?.data?.detail || 'Failed to decline.'); }
    finally { setBusyId(null); }
  };

  return (
    <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid={`card-extra-charges-${booking.id}`}>
      <div className="flex items-center gap-2 mb-2">
        <AlertCircle className="w-4 h-4 text-amber-600" />
        <span className="text-sm font-semibold text-amber-800">
          Extra charge{pending.length > 1 ? 's' : ''} requested by the provider
        </span>
      </div>

      {err && <div className="text-xs text-red-600 mb-2" data-testid={`card-charge-err-${booking.id}`}>{err}</div>}

      {pending.map((c) => (
        <div key={c.id} className="bg-white rounded-lg border p-3 mb-2" data-testid={`card-charge-${c.id}`}>
          <div className="min-w-0">
            <p className="text-sm font-medium text-gray-800 truncate">{c.description || 'Additional charge'}</p>
            <p className="text-xs text-gray-500 mt-0.5">
              You pay {peso(c.amount)}{c.client_fee ? ` · incl. fee ${peso(c.client_fee)}` : ''}
            </p>
          </div>
          <div className="flex gap-2 mt-3">
            <button
              onClick={() => approve(c.id)}
              disabled={busyId === c.id}
              className="flex-1 inline-flex items-center justify-center gap-1 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50"
              data-testid={`card-approve-charge-${c.id}`}
            >
              {busyId === c.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Approve
            </button>
            <button
              onClick={() => reject(c.id)}
              disabled={busyId === c.id}
              className="flex-1 inline-flex items-center justify-center gap-1 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 disabled:opacity-50"
              data-testid={`card-reject-charge-${c.id}`}
            >
              <X className="w-4 h-4" /> Decline
            </button>
          </div>
        </div>
      ))}

      <p className="text-xs text-amber-700 mt-1">
        Approving adds the amount to your “Pay Now” total — you pay the booking and extras together.
      </p>
    </div>
  );
};

export default BookingCardExtraCharges;
